import type { FC } from 'react';
import type { EChartsOption, EChartsType } from 'echarts';
import type { RootState } from '@/store/store';

import { init } from 'echarts';
import { useEffect, useRef, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IChartLabels } from '@/pages/dashboard-search/types';
import { Spinner } from 'flowbite-react';
import ChartTable from '@/components/table';
import {
  DownloadTableIcon,
  TableIcon,
} from '@/pages/dashboard-search/components/chart-wrapper/icons';
import { read, writeFile } from 'xlsx';

type IBarStackChartProps = {
  height: number;
  data: { date: string; value: number }[];
  showChart: boolean;
  labels: IChartLabels;
  title: string;
  isLoading: boolean;
};

const BarStackGoodChart: FC<IBarStackChartProps> = ({
  data,
  height,
  showChart,
  labels,
  title,
  isLoading,
}) => {
  const { currentTheme } = useSelector((state: RootState) => state.theme);
  const sharesRef = useRef<HTMLDivElement | null>(null);
  const [isShowTable, setIsShowTable] = useState(false);

  //   const { colorMap, onlyMyBrandsMap } = useContext(DashboardSearchContext);

  const options = useMemo(() => {
    // const reversedData = [...data].reverse();
    // const legend = [...new Set(reversedData.map(({ date }) => date))];
    const xAxisData = [...new Set(data.map(({ date }) => date))];

    return {
      grid: {
        left: 55,
        right: 5,
        top: 10,
        bottom: 25,
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value) => String(+(+value).toFixed(3)),
      },
      dataset: [
        {
          dimensions: ['date', 'value'],
          source: data,
          id: 'dataset',
        },
        // ...legend.map((brand) => ({
        //   fromId: 'dataset',
        //   id: brand,
        //   transform: {
        //     type: 'filter',
        //     config: { dimension: 'brand', value: brand },
        //   },
        // })),
      ],
      series: {
        data: data.map((dataEl) => dataEl.value),
        type: 'bar',
      },
      //   legend: {
      //     data: legend,
      //     type: 'scroll',
      //     bottom: 0,
      //     textStyle: {
      //       color: currentTheme === 'dark' ? 'white' : 'black',
      //     },
      //   },
      xAxis: {
        type: 'category',
        data: xAxisData,
        // name: labels?.x,
        // nameLocation: 'middle',
        // nameGap: 25,
        // axisLine: {
        //   onZero: false,
        // },
      },
      yAxis: {
        // show: false,
        type: 'value',
        min: () => {
          const min = Math.min(
            ...data
              .map((dataEl) => dataEl.value)
              .filter((value) => value !== -1 && value !== 0)
          );

          const max = Math.max(
            ...data
              .map((dataEl) => dataEl.value)
              .filter((value) => value !== -1 && value !== 0)
          );
          return min - (max - min) * 0.5;
        },
        // min: (value) => value.min - (value.max - value.min) * 0.05,
        // min: 'auto',
        // name: labels?.y,
        // max: 100,
        // nameLocation: 'middle',
        // nameRotate: 90,
        // nameGap: 35,
      },
      // dataZoom: [
      //   {
      //     type: 'inside',
      //   },
      //   {
      //     type: 'inside',
      //   },
      // ],
    } as EChartsOption;
  }, [data, currentTheme]);

  useEffect(() => {
    let myChart: EChartsType;

    if (!isLoading) {
      myChart = init(sharesRef.current);
      showChart && myChart.setOption(options);
    }

    return () => myChart?.dispose && myChart?.dispose?.();
  }, [options, height, showChart, isLoading]);

  const tableHeaders = [labels.x, labels.y];
  const tableData = data.map((dataEl) => [dataEl.date, dataEl.value]);

  const downloadTableDataInXlsx = () => {
    let excelData = '';
    tableHeaders.forEach((rowItem) => {
      excelData += rowItem?.replaceAll(',', '') + ',';
    });
    excelData += '\r\n';
    tableData.forEach((rowItem) => {
      rowItem.forEach((colItem) => {
        excelData += String(colItem)?.replaceAll(',', '') + ',';
      });
      excelData += '\r\n';
    });
    // excelData = 'data:text/xlsx,' + encodeURI(excelData);
    // const a = document.createElement('A');
    // a.setAttribute('href', excelData);
    // a.setAttribute('download', `table_${Date.now()}.xlsx`);
    // document.body.appendChild(a);
    // a.click();

    const wb = read(excelData, { type: 'string' });
    writeFile(wb, `table_${Date.now()}.xlsx`);
  };

  const iconButtons = [
    {
      onClick: () => setIsShowTable((prev) => !prev),
      className: `${
        data?.length ? '' : 'pointer-events-none opacity-50 transition-opacity'
      }`,
      icon: <TableIcon />,
    },
    {
      onClick: downloadTableDataInXlsx,
      className: `ml-3 ${
        data?.length ? '' : 'pointer-events-none opacity-50 transition-opacity'
      }`,
      icon: <DownloadTableIcon />,
    },
  ];

  return (
    <div
      className={`${'min-w-full'} white-block max-w-full grow basis-0 rounded-lg bg-white p-5 shadow`}
    >
      <div className="relative flex items-center justify-between">
        <div>
          <div className="pb-2 text-sm font-bold leading-9">{title}</div>
        </div>
        <div className="absolute right-0 top-0 flex flex-wrap items-center justify-center gap-1.5">
          <div className="ml-3 flex items-center">
            {iconButtons.map(({ onClick, className, icon }, index) => (
              <button key={index} onClick={onClick} className={className}>
                {icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className={`${
          height === 500
            ? isShowTable
              ? 'min-h-[300px]'
              : 'min-h-[120px]'
            : isShowTable
            ? 'min-h-[300px]'
            : 'min-h-[100px]'
        } relative flex h-full flex-col items-center justify-center`}
      >
        {isLoading && (
          <div
            className={`absolute z-10 flex h-full w-full items-center justify-center`}
          >
            <Spinner size="xl" />
          </div>
        )}
        {!isLoading && (
          //  activeTabConfig?.length !== 0 &&
          <>
            <div
              className={`${
                height === 500
                  ? isShowTable
                    ? 'h-[300px]'
                    : 'h-[120px]'
                  : isShowTable
                  ? 'h-[300px]'
                  : 'h-[100px]'
              } w-full`}
              ref={sharesRef}
            />

            {isShowTable && (
              <ChartTable tableHeaders={tableHeaders} tableData={tableData} />
            )}

            {/* <div className="mt-2 min-h-[40px] text-xs italic text-black">
              {description}
            </div> */}
          </>
        )}
        {/* {!isLoading && !isNoFilters && activeTabConfig.length === 0 && (
          <div className="flex h-full w-full items-center justify-center text-black dark:text-white">
            Нет данных по этим фильтрам
          </div>
        )}
        {!isLoading && isNoFilters && (
          <div className="flex h-full w-full items-center justify-center text-black dark:text-white">
            Выберите все необходимые фильтры для загрузки графиков
          </div>
        )} */}
      </div>
    </div>
  );
};

export default BarStackGoodChart;
